/**
 * El texto del aviso de Gustavo, recortado para que entre en la burbuja.
 *
 * (08/09/2026) La burbuja tiene 320 px y el aviso se salía por el costado. El caso real:
 *
 *   "Norealys Wilchez agregó una nota En tu cliente Claudia Apto 3 Dorm Centro.
 *    https://www.inmobiliariaimperium.com/customers/6a4d71bcb2fc9b7521…"
 *
 * El link se comía media pantalla porque una URL no tiene espacios: el navegador no la puede
 * cortar y la empuja fuera del borde. El texto quedaba tapado a la derecha y el aviso, que es
 * lo único que ese globo tiene para decir, se leía a medias.
 *
 * Dos decisiones:
 *
 * 1. **La URL se va.** En un globo de 320 px una dirección web cruda no se lee ni se puede
 *    copiar, y el botón de abajo —"Ver los avisos"— lleva justo ahí. Mostrarla solo ocupaba
 *    el lugar de lo que sí importa. Se saca del preview, no del aviso: el mismo texto sale
 *    por WhatsApp y ahí el link SÍ sirve.
 * 2. **Lo que sobra se corta con puntos suspensivos**, y se corta en una palabra entera: un
 *    tajo en el medio de un nombre se lee como un error del sistema.
 *
 * El renglón de "(y 3 avisos más sin leer)" se agrega DESPUÉS del recorte, a propósito: dice
 * cuántos faltan y es lo primero que se perdería si se recortara todo junto.
 */

/** Cuánto entra sin que el globo se vuelva un párrafo. Medido sobre 320 px y 13,5 px de letra. */
export const LARGO_MAXIMO = 150;

/** Cualquier cosa con forma de dirección web, con protocolo o sin él. */
const URL_SUELTA = /\b(?:https?:\/\/|www\.)\S+/gi;

/**
 * Saca las URLs y deja el texto prolijo: sin espacios dobles ni un punto colgando donde
 * estaba el link.
 */
export function sinEnlaces(texto: string): string {
  return String(texto ?? '')
    .replace(URL_SUELTA, '')
    .replace(/[ \t]{2,}/g, ' ')
    // Un "mirala acá: " que se quedó sin destino no tiene por qué terminar en dos puntos.
    .replace(/\s+([.,;:])/g, '$1')
    .replace(/[:\-–—]\s*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Corta en la última palabra entera que entra y cierra con puntos suspensivos. */
export function recortar(texto: string, largo = LARGO_MAXIMO): string {
  const t = String(texto ?? '').trim();
  if (t.length <= largo) return t;
  const corte = t.slice(0, largo);
  const ultimoEspacio = corte.lastIndexOf(' ');
  // Sin espacios donde cortar (una palabra larguísima) se corta igual: es preferible a
  // devolver el texto entero, que es lo que se salía del globo.
  return `${(ultimoEspacio > largo * 0.6 ? corte.slice(0, ultimoEspacio) : corte).replace(/[\s.,;:]+$/, '')}…`;
}

/** El preview listo para la burbuja: sin links y recortado. */
export function textoParaLaBurbuja(preview: string, largo = LARGO_MAXIMO): string {
  return recortar(sinEnlaces(preview), largo);
}

export interface AvisosSinLeer {
  count:   number;
  preview: string | null;
  lastAt?: string | null;
}

export interface AvisoDeBurbuja {
  key:   string;
  msg:   string;
  chips: Array<{ label: string; prompt?: string }>;
}

/**
 * El aviso tal como ocupa la burbuja, o `null` si no hay ninguno sin leer.
 *
 * Muestra el ÚLTIMO y dice cuántos quedan, que es lo que se pidió: una lista de cuatro avisos
 * en la esquina no se lee, se cierra.
 */
export function avisoEnBurbuja(notices: AvisosSinLeer | undefined | null): AvisoDeBurbuja | null {
  const n = notices?.count ?? 0;
  if (!n || !notices?.preview) return null;

  const texto = textoParaLaBurbuja(notices.preview);
  // Un preview que era SOLO un link se queda sin nada que mostrar. Antes que un globo vacío,
  // se dice lo que hay.
  const cuerpo = texto || `Tenés ${n} aviso${n > 1 ? 's' : ''} sin leer.`;

  return {
    key: `aviso:${notices.lastAt ?? ''}`,
    msg: n > 1 ? `${cuerpo}\n\n(y ${n - 1} aviso${n > 2 ? 's' : ''} más sin leer)` : cuerpo,
    chips: [{ label: n > 1 ? 'Ver los avisos' : 'Ver el aviso', prompt: '__abrir_avisos__' }],
  };
}
